import { useState } from 'react';
import type { ConclusieDiagnoseOption } from '../../../../../../common/enums/conclusieDiagnoseOption';
import type { IMedication } from '../../../../interfaces/medication';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { FilesUrlBase } from '../../../../../../common/enums/filesUrlBase';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ServiceCode } from '../../../../../../common/enums/serviceCode';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import {
    ButtonContainer,
    Form,
    FormDivider,
    FormGroup,
    HeaderDivider,
    InputCheckbox,
} from '../../../../elements/common';
import { FormContextProvider } from '../../../../elements/formContext';
import { TextAreaField } from '../../../../fields/TextArea';
import { ProfessionalSummaryData } from '../../../professionalSummaryData/ProfessionalSummaryData';
import { AssetDropzoneWithActions } from '../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { DiagnosisConclusionSelection } from '../../utils/v1/DiagnosisConclusionSelection';
import { MedicationsAddEdit } from '../../utils/v1/medications/MedicationsAddEdit';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { SpecialismOrganizationProvider } from '../../utils/v1/SpecialismOrganizationProvider';

export interface IGtcV1RequestAddEditCustomControlData {
    conclusie_diagnose: ConclusieDiagnoseOption;
    toelichting: string | null;
    vraag: string | null;
}

export function GtcV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const [showDropzone, setShowDropzone] = useState<boolean>(props.request.assets.length !== 0);
    const [buttonDisabled, setButtonDisabled] = useState<boolean>();

    return (
        <NewOrEditRequestProcessContainer
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={getServiceLabel(props.service.code, 'request', props.service.name)}
        >
            {addEditForm()}

            <ButtonContainer
                alignRight
                buttons={[
                    <CancelButton
                        cancel={props.onCancel}
                        entity={ProcessEntity.request}
                        isExisting={!!props.request.id}
                        serviceCode={props.service.code}
                        serviceType={props.service.serviceType}
                    />,
                    <SaveButton
                        buttonDisabled={buttonDisabled}
                        save={async () => {
                            await saveRequest('draft');
                        }}
                    />,
                    <SaveSendButton
                        buttonDisabled={buttonDisabled}
                        saveSend={async () => {
                            await saveRequest('final');
                        }}
                    />,
                ]}
            />
        </NewOrEditRequestProcessContainer>
    );

    async function saveRequest(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setButtonDisabled(false);

            return;
        }
        const requestId = await props.onSaveRequest(saveType);

        if (saveType === 'draft') {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
            await props.onSave(requestId);
        } else {
            const result = await getJson(
                `/api/organizations/${props.request.selectedDiagnosticReportProviderOrganizationId}`
            );
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'request',
                translateKey('toast.consultRequestSubmit', result.json.name)
            );
            await props.onSave(requestId);
        }
    }

    function addEditForm() {
        const customControlData: IGtcV1RequestAddEditCustomControlData = props.request.customControlData;

        return (
            <FormContextProvider
                value={{
                    placeholderGroupName: 'request',
                    item: customControlData,
                    errors: props.errors,
                    onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                    onDoValidate: async (key) => await props.validateOneCustomControl(key),
                }}
            >
                <Form onSubmit={async () => null}>
                    <SpecialismOrganizationProvider
                        errors={props.errors}
                        initialOrganizationId={props.request.selectedDiagnosticReportProviderOrganizationId}
                        onChange={async (organizationId) => {
                            await props.handleChange('selectedDiagnosticReportProviderOrganizationId', organizationId);
                        }}
                        onDoValidate={async () =>
                            await props.validateOne('selectedDiagnosticReportProviderOrganizationId')
                        }
                        serviceCode={ServiceCode.GTC}
                    />

                    <CareProviderSelector
                        {...props}
                        entity={props.request}
                    />

                    <FormDivider />

                    <HeaderDivider text="Onderzoek" />
                    <FormGroup>
                        <InputCheckbox
                            checked={showDropzone}
                            disabled={props.request.assets && props.request.assets.length !== 0}
                            onChange={async (value) => setShowDropzone(value)}
                            text={translateKey('procedure.sendExtraAttachments')}
                        />
                    </FormGroup>
                    {showDropzone && (
                        <AssetDropzoneWithActions
                            assets={props.request.assets}
                            assetsBaseUrl={FilesUrlBase.requests}
                            onChange={async (assets) => await props.handleChange('assets', assets)}
                            onUploading={async (isUploading) => setButtonDisabled(isUploading)}
                            processActivityId={props.request.id}
                            service={props.service}
                            showUseExistingExamination={false}
                            updateNewRequestPreviousRequest={props.updateNewRequestPreviousRequest}
                        />
                    )}

                    <FormDivider />

                    <MedicationsAddEdit
                        entity={ProcessEntity.request}
                        entityId={props.request.id}
                        errors={props.errors}
                        handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                            await props.handleChange('patientUsesMedication', patientUsesMedication)
                        }
                        onChange={async (medications: IMedication[]) =>
                            await props.handleChange('requestMedications', medications)
                        }
                        patientUsesMedication={props.request.patientUsesMedication}
                        validateMedications={async () => await props.validateOne('requestMedications')}
                        validatePatientUsesMedication={async () => await props.validateOne('patientUsesMedication')}
                    />

                    <ProfessionalSummaryData
                        entity={ProcessEntity.request}
                        entityId={props.request.id}
                        errors={props.errors}
                        handleChangeAllergies={async (allergies) => await props.handleChange('allergies', allergies)}
                        handleChangeEpisodes={async (episodesAndJournal) =>
                            await props.handleChange('requestEpisodesAndJournal', episodesAndJournal)
                        }
                        handleChangeLabResults={async (labResults) =>
                            await props.handleChange('requestLabResults', labResults)
                        }
                        validateEpisodesAndJournals={async () => await props.validateOne('requestEpisodesAndJournal')}
                    />

                    <HeaderDivider text="Vraag" />
                    <DiagnosisConclusionSelection
                        conclusie_diagnose={props.request.customControlData.conclusie_diagnose}
                        diagnosis={props.request.diagnosis}
                        errors={props.errors}
                        handleChangeConclusieDiagnose={props.handleChangeCustomControl}
                        handleChangeDiagnosisConsideration={async (diagnosis) =>
                            await props.handleChange('diagnosis', diagnosis)
                        }
                        service={props.service}
                        validateConclusieDiagnose={props.validateOneCustomControl}
                        validateDiagnosisConsideration={async () => props.validateOne('diagnosis')}
                    />
                    <TextAreaField
                        error={props.errors.get('customControlData.vraag')}
                        fieldKey="vraag"
                        hasErrors={props.errors.has('customControlData.vraag')}
                        isRequired
                        label="Vraag"
                        placeholder="Stel een vraag aan een specialist..."
                    />
                    <TextAreaField
                        error={props.errors.get('customControlData.toelichting')}
                        fieldKey="toelichting"
                        hasErrors={props.errors.has('customControlData.toelichting')}
                        label="Toelichting"
                        placeholder="Een toelichting, opmerkingen of vragen..."
                    />
                </Form>
            </FormContextProvider>
        );
    }
}

export function gtcV1RequestInitializeCustomControlData() {
    return {
        vraag: null,
        toelichting: null,
        conclusie_diagnose: null,
    };
}
